
import React from 'react';
import { GenogramShape, ShapeType } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { FONT_SIZES } from '../constants';

const ShapeComponent: React.FC<{ 
  shape: GenogramShape; 
  isSelected: boolean; 
}> = ({ shape, isSelected }) => {
  const { t } = useLanguage();
  const { x, y, width, height, isDeceased, type, age, text, isCohabitingWithIndex } = shape;
  const baseFontSize = shape.fontSize || FONT_SIZES.medium;
  const ageFontSize = baseFontSize + 6;
  const nameFontSize = baseFontSize + 2;
  const cohabitingFontSize = baseFontSize;
  const nameOffsetY = Math.max(16, baseFontSize + 2);
  const cohabitingOffsetY = Math.max(16, baseFontSize + 2);
  const halfW = width / 2;
  const halfH = height / 2;
  const innerW = width * 0.8;
  const innerH = height * 0.8;
  const innerHalfW = innerW / 2;
  const innerHalfH = innerH / 2;

  let shapeElement;
  switch (type) {
    case ShapeType.Male:
      shapeElement = <rect x={-halfW} y={-halfH} width={width} height={height} rx="2" ry="2" fill="#f9f9f9" stroke="black" strokeWidth="2" />;
      break;
    case ShapeType.Female:
      shapeElement = <circle cx="0" cy="0" r={halfW} fill="#f9f9f9" stroke="black" strokeWidth="2" />;
      break;
    case ShapeType.IndexMale:
      shapeElement = (
        <g>
          <rect x={-halfW} y={-halfH} width={width} height={height} rx="2" ry="2" fill="#f9f9f9" stroke="black" strokeWidth="2" />
          <rect x={-innerHalfW} y={-innerHalfH} width={innerW} height={innerH} rx="1" ry="1" stroke="black" strokeWidth="1.5" fill="none" />
        </g>
      );
      break;
    case ShapeType.IndexFemale:
      shapeElement = (
        <g>
          <circle cx="0" cy="0" r={halfW} fill="#f9f9f9" stroke="black" strokeWidth="2" />
          <circle cx="0" cy="0" r={halfW * 0.7} stroke="black" strokeWidth="1.5" fill="none" />
        </g>
      );
      break;
  }
  
  const hasName = !!text;
  const hasAge = !!age;

  let extraHeight = 0;
  if (hasName) extraHeight += Math.max(18, baseFontSize + 4);
  if (isCohabitingWithIndex) extraHeight += Math.max(14, baseFontSize + 2);

  return (
    <g transform={`translate(${x}, ${y})`}>
      {shapeElement}
      
      {/* Age - inside the shape */}
      {hasAge && (
        <text
          y={2}
          textAnchor="middle"
          fontSize={`${ageFontSize}px`}
          fontWeight="bold"
          fill="#333"
          dominantBaseline="middle"
          style={{pointerEvents: 'none'}}
        >
          {age}
        </text>
      )}

      {/* Name - below the shape */}
      {hasName && (
        <text
            y={halfH + nameOffsetY}
            textAnchor="middle"
            fontSize={`${nameFontSize}px`}
            fontWeight="bold"
            fill="#333"
            dominantBaseline="alphabetic"
            style={{pointerEvents: 'none'}}
            stroke="#ffffff"
            strokeWidth="4px"
            strokeLinejoin="round"
            paintOrder="stroke"
        >
            {text}
        </text>
      )}

      {isDeceased && (
        <g stroke="black" strokeWidth="2.5">
          <line x1={-halfW * 0.8} y1={-halfH * 0.8} x2={halfW * 0.8} y2={halfH * 0.8} />
          <line x1={-halfW * 0.8} y1={halfH * 0.8} x2={halfW * 0.8} y2={-halfH * 0.8} />
        </g>
      )}
      
      {shape.isCohabitingWithIndex && (
        <text 
            y={halfH + cohabitingOffsetY + (hasName ? Math.max(16, baseFontSize + 2) : 0)} 
            textAnchor="middle" 
            fontSize={`${cohabitingFontSize}px`} 
            fontWeight="bold"
            fill="black"
            stroke="#ffffff"
            strokeWidth="4px"
            strokeLinejoin="round"
            paintOrder="stroke"
            dominantBaseline="alphabetic"
        >
          ({t('boundary')})
        </text>
      )}
      {isSelected && <rect x={-halfW - 4} y={-halfH - 4} width={width + 8} height={height + 8 + extraHeight} fill="none" stroke="#4f46e5" strokeWidth="1.5" strokeDasharray="4 4" style={{pointerEvents: 'none'}} />}
    </g>
  );
};

export default ShapeComponent;
